"use client";

import { SYNC_WAVES, useAISyncStatus, useIsFirstBoot } from "@stockline/web";
import { memo, useEffect, useState } from "react";
import { AmbientPill } from "./AmbientPill";
import { BootOverlay } from "./BootOverlay";
import { useBootRecovery } from "./useBootRecovery";
import { useBootSyncSnapshot } from "./useBootSyncSnapshot";
import { useFadeOut } from "./useFadeOut";

// Keep the overlay in the DOM long enough to play its fade-out before
// unmounting. Must match the CSS transition duration in BootOverlay.
const FADE_OUT_MS = 450;

// Minimum time the branded splash stays up, even when everything is ready
// instantly (returning user, warm cache). Long enough to read as an
// intentional splash rather than a flash; short enough not to feel slow.
const MIN_SPLASH_MS = 600;

/**
 * Global startup splash. On every full page load it shows the Stockline logo
 * for a minimum beat — this single, always-present splash is what removes the
 * boot flicker entirely: there is no race between "show overlay" and "show
 * app", just one branded screen that either fades to reveal the app (returning
 * user) or grows the progress stepper in place (genuine cold start).
 *
 * The progress stepper is bootstrap-only: it slides in once we've confirmed
 * this is a true first boot (no critical resource has completed a full fetch
 * yet) with data still to pull. The splash dismisses once the critical wave is
 * ready AND the minimum display time has elapsed, handing off to the app shell
 * with an unobtrusive ambient pill while warm / background waves catch up.
 */
function GlobalBootScreenImpl() {
  const { status } = useAISyncStatus();
  const isFirstBoot = useIsFirstBoot(SYNC_WAVES.critical);
  const snapshot = useBootSyncSnapshot();

  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMinTimeElapsed(true), MIN_SPLASH_MS);
    return () => clearTimeout(timer);
  }, []);

  // The splash holds until the data the app needs is ready AND it has shown
  // for its minimum beat. It does NOT depend on the first-boot verdict, so the
  // undecided window (before Dexie resolves which case this is) never flashes.
  const splashActive = !(snapshot.criticalReady && minTimeElapsed);
  // The stepper is reserved for a confirmed cold start with work left to do.
  const showProgress = isFirstBoot && !snapshot.criticalReady;

  // Watchdog: if the splash sits un-ready too long (blocked DB, dead network,
  // a critical resource wedged in `error`), surface a user-facing escape hatch
  // so the boot can never hang with no way out.
  const recovery = useBootRecovery(splashActive);

  // Linger + fade the splash once it goes inactive, starting the fade only
  // after the app shell behind has painted (see useFadeOut).
  const splash = useFadeOut(splashActive, FADE_OUT_MS);
  const showAmbient =
    !splashActive && status === "syncing" && !snapshot.allReady;

  return (
    <>
      {splash.mounted && (
        <BootOverlay
          exiting={splash.exiting}
          showProgress={showProgress}
          critical={snapshot.critical}
          warm={snapshot.warm}
          background={snapshot.background}
          activeWaveResources={snapshot.activeWaveResources}
          recovery={recovery}
        />
      )}
      {showAmbient && <AmbientPill snapshot={snapshot} />}
    </>
  );
}

export const GlobalBootScreen = memo(GlobalBootScreenImpl);
